import { CONFIG } from "../config";
import type { NoteEvent } from "../types";
import { relativeArticulation } from "./articulation";
import { relativeDynamics } from "./dynamics";

// Real-time scored mode. Notes flow past the hit line
// continuously; each press is judged against a timing window around the note's
// onset instead of gating the playhead. Pure logic — no DOM, no clock — so it
// is unit-testable in isolation.

/** How the transport advances. (`auto` flows the playhead for the demo reel —
 *  judges nothing, writes no score; set programmatically, never persisted.) */
export type PlayMode = "wait" | "scored" | "auto";

/** Outcome of judging a note in scored mode. */
export type Judgement = "perfect" | "good" | "miss";

/** Running tally for the current scored run. */
export interface ScoreStats {
  perfect: number;
  good: number;
  misses: number;
  /** Presses that matched no expected note within the window. */
  wrong: number;
  /** Sum of |timing error| (seconds) over hits, for the average. */
  sumAbsError: number;
}

interface Target {
  note: NoteEvent;
  judged: boolean;
  result: Judgement | null;
  /** |timing error| in seconds for a hit (for the coach's timing gate). */
  error: number;
  /** Onset velocity (0..1) the player struck this note with (1 = unknown). */
  velocity: number;
  /** How long the player held this note, in seconds (0 = not yet released). */
  held: number;
}

function blankStats(): ScoreStats {
  return { perfect: 0, good: 0, misses: 0, wrong: 0, sumAbsError: 0 };
}

/**
 * Judges presses against a fixed timing window. Driven by the engine: presses
 * are reported via {@link judgePress} with the current song time, and
 * {@link sweepMisses} retires notes whose window has fully passed unhit.
 */
export class Scorer {
  readonly stats: ScoreStats = blankStats();
  private targets: Target[] = [];
  private byNote = new Map<NoteEvent, Target>();
  private wrongRing: { pitch: number; time: number }[] = [];
  /** The target most recently matched for a pitch, so a later release can record
   *  its held duration (the player's note-off arrives after the press is judged). */
  private lastJudged = new Map<number, Target>();
  /** Bumped on every judgement-state change so per-frame readers can memoize. */
  private judgeVersion = 0;
  private exprMemo: { version: number; value: { dynamics: number; articulation: number } } | null =
    null;
  /** Monotonic index past which every target's miss window has closed — `sweepMisses`
   *  resumes here instead of rescanning the whole (onset-sorted) target list each frame. */
  private missCursor = 0;

  constructor(notes: readonly NoteEvent[]) {
    this.load(notes);
  }

  /** Rebuild the target list (e.g. after a hand-filter change) and reset. */
  load(notes: readonly NoteEvent[]): void {
    this.targets = notes.map((note) => ({
      note,
      judged: false,
      result: null,
      error: 0,
      velocity: 1,
      held: 0,
    }));
    this.byNote = new Map(this.targets.map((t) => [t.note, t]));
    this.reset();
  }

  /** Clear all judgements and the tally (for a fresh run). */
  reset(): void {
    for (const t of this.targets) {
      t.judged = false;
      t.result = null;
      t.velocity = 1;
      t.held = 0;
    }
    Object.assign(this.stats, blankStats());
    this.wrongRing = [];
    this.lastJudged.clear();
    this.missCursor = 0;
    this.exprMemo = null;
    this.judgeVersion++;
  }

  /** Judge a key press of `pitch` at song time `time` (seconds), struck at onset
   *  `velocity` (0..1; defaults to 1 when the source doesn't carry it). */
  judgePress(pitch: number, time: number, velocity = 1): void {
    this.judgeVersion++; // a press always changes state (a hit or a wrong)
    const window = CONFIG.hitWindowMs / 1000;
    const perfect = CONFIG.perfectWindowMs / 1000;

    let best: Target | null = null;
    let bestError = Number.POSITIVE_INFINITY;
    for (const t of this.targets) {
      if (t.judged || t.note.midi !== pitch) continue;
      const error = Math.abs(t.note.onset - time);
      if (error <= window && error < bestError) {
        best = t;
        bestError = error;
      }
    }

    if (!best) {
      this.stats.wrong++;
      this.wrongRing.push({ pitch, time });
      if (this.wrongRing.length > CONFIG.staff.wrongRingSize) this.wrongRing.shift();
      return;
    }
    best.judged = true;
    best.result = bestError <= perfect ? "perfect" : "good";
    best.error = bestError;
    best.velocity = velocity;
    this.lastJudged.set(pitch, best);
    this.stats[best.result]++;
    this.stats.sumAbsError += bestError;
  }

  /** Record how long the player held `pitch` (seconds) — its note-off, which
   *  arrives after the press was judged. Feeds the articulation measure. */
  recordHold(pitch: number, heldSec: number): void {
    const t = this.lastJudged.get(pitch);
    if (t) {
      t.held = heldSec;
      this.judgeVersion++;
    }
  }

  /** Relative dynamics + articulation over the hits of the current run, 0..1 each
   *  (1 = nothing to grade / matched the expected shape). Pure measures; see
   *  dynamics.ts / articulation.ts. */
  expressionScores(): { dynamics: number; articulation: number } {
    // Memoize: this is read every scored frame for the HUD but only changes when a
    // judgement does (a full sort+map over all targets otherwise runs 60×/sec).
    if (this.exprMemo && this.exprMemo.version === this.judgeVersion) return this.exprMemo.value;
    const hits = this.targets
      .filter((t) => t.judged && t.result !== null && t.result !== "miss")
      .sort((a, b) => a.note.onset - b.note.onset);

    const dynamics = relativeDynamics(
      hits.map((t) => t.velocity),
      hits.map((t) => t.note.velocity),
    );

    const onsets = [...new Set(this.targets.map((t) => t.note.onset))].sort((a, b) => a - b);
    const nextOnset = (o: number): number => onsets.find((x) => x > o) ?? o;
    const articulation = relativeArticulation(
      hits
        .filter((t) => t.held > 0)
        .map((t) => ({
          playerHeld: t.held,
          expectedHeld: t.note.duration,
          interOnset: nextOnset(t.note.onset) - t.note.onset,
        })),
    );
    const value = { dynamics, articulation };
    this.exprMemo = { version: this.judgeVersion, value };
    return value;
  }

  /** Count of hits whose |timing error| is within `thresholdSec` (the coach's
   *  timing gate). At `thresholdSec ≥ hitWindow` this is every hit. */
  onTimeHits(thresholdSec: number): number {
    let n = 0;
    for (const t of this.targets) {
      if (t.judged && t.result !== null && t.result !== "miss" && t.error <= thresholdSec) n++;
    }
    return n;
  }

  /** Exclude notes before `time` from scoring (mark judged, no result) — used when
   *  a windowed rep / A–B loop leads in over earlier notes that shouldn't count. */
  skipBefore(time: number): void {
    for (const t of this.targets) {
      if (!t.judged && t.note.onset < time) {
        t.judged = true;
        t.result = null;
      }
    }
  }

  /** Retire any note whose hit window closed before `time` as a miss. */
  sweepMisses(time: number): void {
    const window = CONFIG.hitWindowMs / 1000;
    // Targets are onset-sorted, so miss deadlines (onset+window) are ascending: resume at
    // the cursor and stop at the first target still within its window. O(newly-passed),
    // not O(all targets), every frame. reset() rewinds the cursor.
    while (this.missCursor < this.targets.length) {
      const t = this.targets[this.missCursor];
      if (!t || t.note.onset + window >= time) break;
      if (!t.judged) {
        t.judged = true;
        t.result = "miss";
        this.stats.misses++;
        this.judgeVersion++;
      }
      this.missCursor++;
    }
  }

  /** Judgement for a specific note (for rendering), or null if pending. */
  judgementFor(note: NoteEvent): Judgement | null {
    return this.byNote.get(note)?.result ?? null;
  }

  /** Recent wrong presses (pitch + song time) for the staff overlay — non-consuming. */
  recentWrong(): readonly { pitch: number; time: number }[] {
    return this.wrongRing;
  }

  /** Pitches whose hit window currently overlaps `time` and are still open. */
  expectedAt(time: number): Set<number> {
    const window = CONFIG.hitWindowMs / 1000;
    const pitches = new Set<number>();
    const lo = time - window;
    const hi = time + window;
    // Onset-sorted: binary-search the first onset ≥ lo, then walk while ≤ hi — instead of
    // scanning every target each frame.
    let a = 0;
    let b = this.targets.length;
    while (a < b) {
      const m = (a + b) >> 1;
      if ((this.targets[m]?.note.onset ?? 0) < lo) a = m + 1;
      else b = m;
    }
    for (let i = a; i < this.targets.length; i++) {
      const t = this.targets[i];
      if (!t || t.note.onset > hi) break;
      if (!t.judged) pitches.add(t.note.midi);
    }
    return pitches;
  }
}
